/** Elbow pins: two handles, a constrained station drag, and a local flag.
 * Geometry proposes; only the flag commits. Main owns rebuilds and mixer holds.
 */
import {
  BufferGeometry,
  Group,
  Line,
  LineBasicMaterial,
  Raycaster,
  Vector2,
  Vector3,
  type Object3D,
} from 'three';
import type { AnimatorDocument, ResolvedHinge, RigDescriptor } from '@harryhausen/kernel';
import { elbowSection, elbowTube, insideElbowSection, type ElbowTube } from './elbow-section.js';
import type { Viewport } from './viewport.js';
import { ELBOW_HUE } from './marker-hues.js';

type Side = 'Left' | 'Right';
type Run = {
  scene: Object3D;
  rig: RigDescriptor;
  hinges: readonly ResolvedHinge[];
  targetImport: { rig: RigDescriptor };
  confirmedElbows: readonly string[];
};
interface Deps {
  viewport: Viewport;
  run(): Run;
  document(): AnimatorDocument;
  buttons(): Iterable<HTMLButtonElement>;
  apply(doc: AnimatorDocument): Promise<boolean>;
  rest(): void;
  finish(): void;
  status(message: string): void;
  ready(): boolean;
}
type Pin = {
  side: Side;
  tube: ElbowTube;
  target: Vector3;
  fraction: number;
  ring: Line<BufferGeometry, LineBasicMaterial>;
  dot: HTMLButtonElement;
  confirmed: boolean;
  suspect: boolean;
  valid: boolean;
  error: string | null;
  /**
   * The section at the joint refused (open, branching, no area, several
   * tubes) and the pin was placed ROUGHLY instead — at the nearest clean
   * station, or on the bone itself. Allen's ruling, 2026-09-08: "the default
   * should be to rough position the elbow even if we are uncertain about
   * position." A rough pin can be dragged and committed like any other; it
   * is always `suspect`, so the button pulses and the person looks.
   */
  rough: boolean;
  /** A person moved it (drag or arrow keys) since it was last pinned or placed — a draft. */
  moved: boolean;
};

/** One elbow pin as the Body header reads it (body-progress.ts, §3.1 Elbows row). */
export interface ElbowPinStatus {
  readonly confirmed: boolean;
  readonly suspect: boolean;
  readonly placed: boolean;
  readonly refused: boolean;
}

const ROUGH_NOTE = 'Rough station — no clean cross-section here; the ring is a placeholder.';
const ORBIT_NOTE = 'Orbit to see the arm from the side.';

/** A station on the bone, for a rough pin: the joint moved along the arm's axis. */
function roughStation(tube: ElbowTube, fraction: number): Vector3 {
  return tube.joint.clone().addScaledVector(tube.axis, fraction * tube.length);
}

/** A placeholder ring for a rough pin — a circle where no clean section exists. */
function roughContour(tube: ElbowTube, centre: Vector3, radius: number): Vector3[] {
  const points: Vector3[] = [];
  for (let i = 0; i < 24; i += 1) {
    const a = (i / 24) * Math.PI * 2;
    points.push(centre.clone().addScaledVector(tube.u, Math.cos(a) * radius).addScaledVector(tube.v, Math.sin(a) * radius));
  }
  return points;
}

/**
 * The nearest station to the joint at which the arm reads as one clean tube,
 * scanned outward in both directions, or null when none does within the
 * marker's reach. The scan is the fallback, never the first choice.
 */
function nearestCleanStation(tube: ElbowTube): { fraction: number; section: ReturnType<typeof elbowSection> } | null {
  for (let step = 1; step <= 25; step += 1) {
    for (const sign of [1, -1]) {
      const fraction = sign * step * 0.01;
      try {
        return { fraction, section: elbowSection(tube, fraction) };
      } catch {
        // keep scanning
      }
    }
  }
  return null;
}

/** Same normalized authored-offset door as the tested placement experiment. */
function documentWithPin(doc: AnimatorDocument, run: Run, pin: Pin): AnimatorDocument {
  const original = run.targetImport.rig.bones.find((b) => b.name === pin.tube.driver);
  const endName = run.rig.mapping.find((m) => m.ontologyId === `${pin.side}Hand`)?.rigBone;
  const end = run.rig.bones.find((b) => b.name === endName);
  if (!original || !end) throw new Error('Cannot resolve the original elbow bind');
  const length = new Vector3().copy(original.worldBind.position).distanceTo(end.worldBind.position);
  const offset = pin.target
    .clone()
    .sub(original.worldBind.position)
    .applyQuaternion(pin.tube.rotation.clone().invert())
    .divideScalar(length);
  return {
    ...doc,
    target: {
      ...doc.target,
      recenters: [
        ...(doc.target.recenters ?? []).filter((r) => r.bone !== pin.tube.driver),
        {
          bone: pin.tube.driver,
          method: 'limb-axis',
          confirmedBy: 'human:elbow-pin',
          authoredOffset: { x: offset.x, y: offset.y, z: offset.z },
          rebuildHinge: true,
        },
      ],
    },
  };
}

export function createElbowPlacementTool(deps: Deps) {
  const surface = deps.viewport.editingSurface();
  const group = new Group();
  surface.overlays.add(group);
  group.visible = false;
  const style = document.createElement('style');
  style.textContent = `
    .elbow-pin {position:fixed;z-index:1200;width:30px;height:30px;padding:0;border:3px solid #d9fcffcc;border-radius:50%;background:#55cdd74d;box-shadow:0 0 0 4px #14272b40,0 2px 9px #0004;cursor:grab;touch-action:none;transform:translate(-50%,-50%)}
    .elbow-pin[aria-pressed="true"] {border-color:#fff;box-shadow:0 0 0 5px #6be6ef60,0 2px 9px #0009}
    .elbow-pin[data-pinned="true"] {border-style:solid;border-color:#fff;box-shadow:0 0 0 3px #65e6ed,0 2px 9px #0006}
    .elbow-pin:focus-visible {outline:3px solid #fff;outline-offset:6px}
    .elbow-pin[hidden],.elbow-flyout[hidden] {display:none}
    .elbow-flyout {position:fixed;left:0;top:0;z-index:1250;display:flex;gap:5px;align-items:center;padding:6px;border:1px solid #76688a;border-radius:7px;background:#201b2bed;box-shadow:0 4px 16px #0008;font:13px system-ui;color:#eee;backdrop-filter:blur(12px)}
    .elbow-flyout button {background:#493c5b;color:#f3eafa;border:1px solid #8a749f;border-radius:5px;padding:7px 12px;cursor:pointer;font:inherit}
    .elbow-flyout button:disabled {opacity:.55;cursor:wait}
    .elbow-flyout .elbow-error {max-width:210px;font-size:12px}
    .elbow-action:disabled {opacity:.55;cursor:default}
    .elbow-action:focus-visible {outline:2px solid #c5abd9;outline-offset:3px}
  `;
  document.head.appendChild(style);
  const flag = document.createElement('div');
  flag.className = 'elbow-flyout';
  flag.hidden = true;
  flag.setAttribute('role', 'group');
  flag.setAttribute('aria-label', 'Pin elbow');
  const confirm = document.createElement('button');
  confirm.type = 'button';
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.textContent = '×';
  dismiss.setAttribute('aria-label', 'Dismiss elbow flag');
  const message = document.createElement('span');
  message.className = 'elbow-error';
  message.setAttribute('role', 'status');
  flag.append(confirm, message, dismiss);
  document.body.appendChild(flag);
  let pins: Pin[] = [],
    mounted: Run | null = null,
    active = false,
    busy = false,
    selected: Pin | null = null;
  let unavailable = '';
  let drag: {
    pin: Pin;
    from: Vector2;
    direction: Vector2;
    pixels: number;
    fraction: number;
    target: Vector3;
    confirmed: boolean;
    moved: boolean;
    pointer: number;
    owner: HTMLElement;
  } | null = null;
  const closeFlag = () => {
    selected = null;
    flag.hidden = true;
  };
  const openFlag = (pin: Pin) => {
    selected = pin;
    flag.hidden = false;
    confirm.textContent = '+Pin';
    confirm.setAttribute('aria-label', `Pin ${pin.side.toLowerCase()} elbow`);
    confirm.disabled = busy || !deps.ready() || !pin.valid;
    message.textContent = pin.error ?? '';
    message.hidden = pin.error === null;
  };
  const drawSection = (pin: Pin, fraction: number, preserve?: Vector3) => {
    try {
      const section = elbowSection(pin.tube, fraction);
      const target = preserve ?? section.center;
      if (!insideElbowSection(pin.tube, section.contour, target))
        throw new Error('The point is outside this arm tube. Move the ring slightly.');
      pin.target.copy(target);
      pin.fraction = fraction;
      pin.valid = true;
      pin.error = null;
      pin.ring.geometry.dispose();
      pin.ring.geometry = new BufferGeometry().setFromPoints([
        ...section.contour,
        section.contour[0]!,
      ]);
    } catch (error) {
      if (pin.rough) {
        // No clean section here either. The pin stays usable: it sits on the
        // bone at this station (or where the drag put it), wears a
        // placeholder ring, and says so — refusing would put us back where
        // Allen found us, with an elbow the armature has and the tool won't.
        pin.target.copy(preserve ?? roughStation(pin.tube, fraction));
        pin.fraction = fraction;
        pin.valid = true;
        pin.error = ROUGH_NOTE;
        pin.ring.geometry.dispose();
        const contour = roughContour(pin.tube, pin.target, pin.tube.length * 0.06);
        pin.ring.geometry = new BufferGeometry().setFromPoints([...contour, contour[0]!]);
      } else {
        pin.valid = false;
        pin.error = error instanceof Error ? error.message : String(error);
      }
    }
    if (selected === pin) openFlag(pin);
  };
  const endMode = () => {
    if (drag) {
      const current = drag;
      drag = null;
      surface.navigation(true);
      if (current.owner.hasPointerCapture(current.pointer))
        current.owner.releasePointerCapture(current.pointer);
      current.pin.confirmed = current.confirmed;
      current.pin.moved = current.moved;
      drawSection(current.pin, current.fraction, current.target);
    }
    active = false;
    closeFlag();
    group.visible = false;
    for (const pin of pins) pin.dot.hidden = true;
    deps.finish();
    refresh(); // Cancel discards uncommitted station previews, including keyboard edits.
  };
  const toggle = () => {
    if (busy || !deps.ready() || pins.length === 0) return;
    if (active) {
      endMode();
      return;
    }
    try {
      deps.rest();
    } catch (error) {
      deps.status(error instanceof Error ? error.message : String(error));
      return;
    }
    active = true;
    group.visible = true;
    for (const pin of pins) pin.dot.hidden = false;
  };
  const refresh = (keepDrafts = false) => {
    const drafts = keepDrafts
      ? new Map(pins.filter((p) => !p.confirmed).map((p) => [p.side, p]))
      : new Map<Side, Pin>();
    closeFlag();
    for (const pin of pins) {
      pin.dot.remove();
      pin.ring.geometry.dispose();
      pin.ring.material.dispose();
    }
    group.clear();
    pins = [];
    unavailable = '';
    mounted = deps.run();
    for (const side of ['Left', 'Right'] as const) {
      try {
        const tube = elbowTube(
          mounted.scene,
          mounted.rig,
          side,
          deps.document().target.selectSkeleton,
        );
        if (!mounted.hinges.some((h) => h.driver === tube.driver))
          throw new Error('This rig has no elbow correction profile');
        const draft = drafts.get(side);
        const confirmed = !draft && mounted.confirmedElbows.includes(tube.driver);
        // The section at the joint, or — when it refuses — the nearest clean
        // one, or — when none is clean — the bone itself. A rough pin is
        // offered rather than no pin: the person can drag it and decide.
        let section: ReturnType<typeof elbowSection> | null = null;
        let fraction = 0;
        let rough = false;
        let roughNote: string | null = null;
        try {
          section = elbowSection(tube, 0);
        } catch (refusal) {
          rough = true;
          const nearest = nearestCleanStation(tube);
          if (nearest) {
            section = nearest.section;
            fraction = nearest.fraction;
            roughNote = `${side} elbow placed roughly: the arm's cross-section at the joint ${refusal instanceof Error ? refusal.message.replace(/\.$/, '').toLowerCase() : 'refused'}; using the nearest clean station, ${Math.round(Math.abs(fraction) * 100)}% ${fraction > 0 ? 'toward the hand' : 'toward the shoulder'}. Drag to adjust, then pin.`;
          } else {
            roughNote = `${side} elbow placed roughly on the bone: no clean arm cross-section within reach of the joint. Drag to adjust, then pin.`;
          }
        }
        // Local transverse diagnostic only: no anatomical station is inferred.
        const centre = section ? section.center : roughStation(tube, 0);
        const offset = centre.distanceTo(tube.joint);
        const suspect =
          rough ||
          (!confirmed &&
            section !== null &&
            (!insideElbowSection(tube, section.contour, tube.joint) ||
              (offset > section.radius * 0.35 && offset > tube.length * 0.03)));
        if (roughNote) unavailable += `${roughNote} `;
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'elbow-pin';
        dot.hidden = !active;
        dot.dataset['side'] = side;
        dot.setAttribute('aria-label', `${side} elbow`);
        dot.title = `${side} elbow — drag along the arm, then pin. Arrow keys adjust; Enter opens the flag.`;
        const ring = new Line(
          new BufferGeometry(),
          new LineBasicMaterial({ color: ELBOW_HUE, depthTest: false, depthWrite: false }),
        );
        ring.renderOrder = 1000;
        group.add(ring);
        document.body.appendChild(dot);
        const pin: Pin = {
          side,
          tube,
          target: centre.clone(),
          fraction,
          ring,
          dot,
          confirmed,
          suspect,
          valid: true,
          error: null,
          rough,
          moved: draft?.moved ?? false,
        };
        if (rough) dot.dataset['rough'] = 'true';
        drawSection(pin, draft?.fraction ?? fraction, confirmed ? tube.joint : draft?.target);
        dot.addEventListener('pointerdown', (event) => {
          if (event.button === 0) beginDrag(event, pin, dot);
        });
        dot.addEventListener('click', (event) => {
          if (event.isTrusted && !busy && active) openFlag(pin);
        });
        dot.addEventListener('keydown', (event) => {
          if (!active || busy || !deps.ready()) return;
          const step =
            event.key === 'ArrowRight' || event.key === 'ArrowUp'
              ? 0.005
              : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
                ? -0.005
                : 0;
          if (step) {
            event.preventDefault();
            pin.confirmed = false;
            pin.moved = true;
            drawSection(pin, Math.max(-0.25, Math.min(0.25, pin.fraction + step)));
            openFlag(pin);
          }
        });
        pins.push(pin);
      } catch (error) {
        unavailable += `${side}: ${error instanceof Error ? error.message : String(error)}. `;
      }
    }
    group.visible = active;
  };
  function beginDrag(event: PointerEvent, pin: Pin, owner: HTMLElement) {
    if (!active || busy || !deps.ready()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeFlag();
    const a = deps.viewport.projectPoint(pin.target),
      b = deps.viewport.projectPoint(
        pin.target.clone().addScaledVector(pin.tube.axis, pin.tube.length),
      );
    if (!a || !b) return;
    const direction = new Vector2(b.x - a.x, b.y - a.y),
      pixels = direction.length();
    if (pixels < 20) {
      pin.error = ORBIT_NOTE;
      openFlag(pin);
      return;
    }
    drag = {
      pin,
      from: new Vector2(event.clientX, event.clientY),
      direction: direction.normalize(),
      pixels,
      fraction: pin.fraction,
      target: pin.target.clone(),
      confirmed: pin.confirmed,
      moved: pin.moved,
      pointer: event.pointerId,
      owner,
    };
    surface.navigation(false);
    owner.setPointerCapture(event.pointerId);
  }
  const ray = new Raycaster();
  surface.canvas.addEventListener(
    'pointerdown',
    (event) => {
      if (!active || busy || event.button !== 0) return;
      const rect = surface.canvas.getBoundingClientRect();
      ray.setFromCamera(
        new Vector2(
          ((event.clientX - rect.left) / rect.width) * 2 - 1,
          (-(event.clientY - rect.top) / rect.height) * 2 + 1,
        ),
        surface.camera,
      );
      group.updateMatrixWorld(true);
      for (const pin of pins) {
        ray.params.Line = { threshold: pin.tube.length * 0.025 };
        if (ray.intersectObject(pin.ring).length) {
          beginDrag(event, pin, surface.canvas);
          return;
        }
      }
      closeFlag();
    },
    true,
  );
  window.addEventListener(
    'pointermove',
    (event) => {
      if (!drag || event.pointerId !== drag.pointer) return;
      event.stopImmediatePropagation();
      const travel = new Vector2(event.clientX, event.clientY).sub(drag.from);
      if (travel.length() < 2) return;
      drag.pin.confirmed = false;
      drag.pin.moved = true;
      drawSection(
        drag.pin,
        Math.max(-0.25, Math.min(0.25, drag.fraction + travel.dot(drag.direction) / drag.pixels)),
      );
    },
    true,
  );
  const release = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointer) return;
    const current = drag;
    drag = null;
    surface.navigation(true);
    event.stopImmediatePropagation();
    if (current.owner.hasPointerCapture(event.pointerId))
      current.owner.releasePointerCapture(event.pointerId);
    if (event.type === 'pointercancel') {
      current.pin.confirmed = current.confirmed;
      current.pin.moved = current.moved;
      drawSection(current.pin, current.fraction, current.target);
      closeFlag();
    } else openFlag(current.pin);
  };
  window.addEventListener('pointerup', release, true);
  window.addEventListener('pointercancel', release, true);
  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape' && active && !busy) {
        event.stopImmediatePropagation();
        endMode();
      }
    },
    true,
  );
  dismiss.onclick = () => closeFlag();
  confirm.onclick = async (event) => {
    if (!event.isTrusted || !selected || !selected.valid || busy || !deps.ready()) return;
    const pin = selected;
    busy = true;
    confirm.disabled = true;
    confirm.textContent = 'Pinning…';
    const restoreView = surface.preserveView();
    try {
      if (await deps.apply(documentWithPin(deps.document(), deps.run(), pin))) {
        pin.confirmed = true;
        pin.moved = false;
        restoreView();
        deps.rest();
        refresh(true);
        if (pins.length === 2 && pins.every((p) => p.confirmed)) endMode();
      } else {
        endMode();
        refresh();
      }
    } catch (error) {
      pin.error = error instanceof Error ? error.message : String(error);
      if (active) openFlag(pin);
      else deps.status(pin.error);
    } finally {
      busy = false;
      if (selected) openFlag(selected);
    }
  };
  const update = () => {
    if (!deps.ready() && active && !busy) endMode();
    if (mounted !== deps.run() && !busy) {
      if (active) endMode();
      refresh();
    }
    for (const button of deps.buttons()) {
      button.classList.add('elbow-action', 'pin-arm');
      button.onclick = toggle;
      button.disabled = busy || !deps.ready() || pins.length === 0;
      button.textContent = active
        ? 'Done pinning'
        : 'Pin elbows';
      button.setAttribute('aria-pressed', String(active));
      button.dataset['suspect'] = String(pins.some((p) => p.suspect));
      button.title =
        unavailable ||
        (pins.some((p) => p.suspect)
          ? 'An elbow joint is outside or substantially off-centre in the measured arm cross-section. Click to place elbow pins.'
          : 'Place or adjust the elbow pins.');
    }
    for (const pin of pins) {
      const point = active ? deps.viewport.projectPoint(pin.target) : null;
      pin.dot.hidden = point === null;
      if (point) {
        pin.dot.style.left = `${point.x}px`;
        pin.dot.style.top = `${point.y}px`;
        const along = deps.viewport.projectPoint(
          pin.target.clone().addScaledVector(pin.tube.axis, pin.tube.length),
        );
        if (along) {
          pin.dot.dataset['axisX'] = String(along.x - point.x);
          pin.dot.dataset['axisY'] = String(along.y - point.y);
        }
      }
      pin.dot.dataset['pinned'] = String(pin.confirmed);
      pin.dot.setAttribute('aria-pressed', String(selected === pin || drag?.pin === pin));
      pin.ring.material.color.set(ELBOW_HUE);
    }
    if (selected && !flag.hidden) {
      const at = deps.viewport.projectPoint(selected.target);
      if (at) {
        const rect = flag.getBoundingClientRect();
        const x = Math.max(8, Math.min(at.x + 28, window.innerWidth - rect.width - 8));
        const y = Math.max(
          8,
          Math.min(at.y - rect.height - 18, window.innerHeight - rect.height - 8),
        );
        flag.style.transform = `translate(${x}px,${y}px)`;
      } else closeFlag();
    }
    return () => {};
  };
  return Object.assign(update, {
    cancel: () => {
      if (active) endMode();
    },
    /**
     * The pins as the Body header reads them (XD note §3.1, Elbows row); empty
     * when the body offers no elbow correction. A new subject is re-measured
     * first — the same catch-up the frame hook does — so a header drawn on the
     * subject change never reads the previous subject's pins.
     */
    status: (): readonly ElbowPinStatus[] => {
      if (mounted !== deps.run() && !busy && !active) refresh();
      return pins.map((p) => ({
        confirmed: p.confirmed,
        suspect: p.suspect,
        placed: p.moved && !p.confirmed,
        refused: p.error !== null && p.error !== ROUGH_NOTE && p.error !== ORBIT_NOTE,
      }));
    },
  });
}
